// Single source of truth for audit_logs.action string literals.

/**
 * Resource types exposed by audit-log filters.
 *
 * This includes current stored `audit_logs.target_type` values, retired
 * names needed to find historical rows, and `agent_version`,
 * which is a semantic read target backed by `agent.version.*` rows whose
 * stored target remains the parent agent.
 */
export const AUDIT_RESOURCE_TYPES = [
  "account",
  "agent",
  "agent_file",
  "agent_linked_resource",
  "agent_skill",
  "agent_thread",
  "agent_version",
  "analyzer_recommendation",
  "analyzer_run",
  "api_key",
  "bridge",
  "classification",
  "classifier",
  "classifier_version",
  "computer_use_run",
  "connection",
  "connection_grant",
  "coverage",
  "custom_app",
  "custom_app_job_run",
  "custom_model",
  "custom_record",
  "directory",
  "directory_group",
  "directory_group_membership",
  "directory_user",
  "document_parser_policy",
  "email",
  "email_message",
  "email_policy",
  "email_policy_attachment",
  "enrichment",
  "entity_file",
  "eval_dataset_item",
  "eval_evaluator",
  "eval_experiment",
  "eval_scenario",
  "extractor",
  "extractor_run",
  "extractor_version",
  "feature_flag",
  "field_definition",
  "file",
  "inbound_sender",
  "integration",
  "integration_config",
  "integration_grant",
  "invitation",
  "lab_fixture",
  "linked_resource",
  "mailbox",
  "model_gateway",
  "model_policy",
  "model_route",
  "oauth_client",
  "oauth_grant",
  "organization",
  "organization_domain",
  "organization_membership",
  "outbound_oauth_connection",
  "outlook_categories_backfill_run",
  "platform_bot_installation",
  "product_line",
  "provider_credential",
  "remote_desktop_target",
  "routine",
  "runner",
  "schedule",
  "schema_assignment",
  "schema_definition",
  "signal",
  "skill",
  "skill_bulk",
  "submission",
  "submission_coverage",
  "submission_output",
  "system_agent",
  "system_agent_bulk",
  "task",
  "task_comment",
  "task_execution",
  "task_linked_resource",
  "task_participant",
  "task_template",
  "triage_run",
  "upload",
  "user",
  "vault_credential",
  "verification",
  "verification_run",
  "watcher",
  "webhook_endpoint",
  "workflow",
  "workflow_run",
  "workspace",
  "workspace_configuration_binding",
  "workspace_configuration_draft",
  "workspace_github_authorization",
  "workspace_github_connection",
  "workspace_github_revision",
  "workspace_membership",
  "workspace_release",
  "workspace_skill",
] as const;

export type AuditResourceType = (typeof AUDIT_RESOURCE_TYPES)[number];

export const AUDIT_ACTIONS = {
  workspace_github: {
    proposal_requested: "workspace_github.proposal_requested",
    repository_create_requested: "workspace_github.repository_create_requested",
    repository_seed_requested: "workspace_github.repository_seed_requested",
    authorization_started: "workspace_github.authorization_started",
    authorized: "workspace_github.authorized",
    connected: "workspace_github.connected",
    disconnected: "workspace_github.disconnected",
    revision_retained: "workspace_github.revision_retained",
    archive_downloaded: "workspace_github.archive_downloaded",
  },
  outbound_oauth_connection: {
    credential_rotated: "outbound_oauth_connection.credential_rotated",
    credentials_tested: "outbound_oauth_connection.credentials_tested",
    created: "outbound_oauth_connection.created",
    disabled: "outbound_oauth_connection.disabled",
    enabled: "outbound_oauth_connection.enabled",
    renamed: "outbound_oauth_connection.renamed",
    updated: "outbound_oauth_connection.updated",
  },
  agent: {
    created: "agent.created",
    deleted: "agent.deleted",
    updated: "agent.updated",
    version: {
      drafted: "agent.version.drafted",
      promoted: "agent.version.promoted",
      published: "agent.version.published",
    },
  },
  agent_file: {
    attached: "agent_file.attached",
    detached: "agent_file.detached",
    label_updated: "agent_file.label_updated",
  },
  agent_linked_resource: {
    created: "agent_linked_resource.created",
    deleted: "agent_linked_resource.deleted",
  },
  agent_skill: {
    created: "agent_skill.created",
    deleted: "agent_skill.deleted",
    updated: "agent_skill.updated",
  },
  agent_thread: {
    approval_approved: "agent_thread.approval_approved",
    approval_denied: "agent_thread.approval_denied",
    canceled: "agent_thread.canceled",
    input_request_replied: "agent_thread.input_request_replied",
    listener_canceled: "agent_thread.listener_canceled",
    message_queued: "agent_thread.message_queued",
    question_answered: "agent_thread.question_answered",
    queued_message_canceled: "agent_thread.queued_message_canceled",
  },
  analyzer_run: {
    created: "analyzer_run.created",
  },
  api_key: {
    created: "api_key.created",
    revoked: "api_key.revoked",
  },
  classification: {
    created: "classification.created",
  },
  classifier: {
    created: "classifier.created",
    updated: "classifier.updated",
  },
  classifier_version: {
    created: "classifier_version.created",
  },
  custom_model: {
    archived: "custom_model.archived",
    created: "custom_model.created",
    records_imported: "custom_model.records_imported",
    updated: "custom_model.updated",
  },
  custom_record: {
    created: "custom_record.created",
    deleted: "custom_record.deleted",
    updated: "custom_record.updated",
  },
  computer_use: {
    canceled: "computer_use.canceled",
    steered: "computer_use.steered",
  },
  integration: {
    archived: "integration.archived",
    created: "integration.created",
    credentials_tested: "integration.credentials_tested",
    deleted: "integration.deleted",
    file_imported: "integration.file_imported",
    mailbox_folders_updated: "integration.mailbox_folders_updated",
    permission_policy_updated: "integration.permission_policy_updated",
    provider_enabled: "integration.provider_enabled",
    resync_triggered: "integration.resync_triggered",
    revoked: "integration.revoked",
    tool_dryrun: "integration.tool_dryrun",
    unarchived: "integration.unarchived",
    updated: "integration.updated",
  },
  integration_grant: {
    created: "integration_grant.created",
    revoked: "integration_grant.revoked",
    updated: "integration_grant.updated",
  },
  entity_file: {
    attached: "entity_file.attached",
    detached: "entity_file.detached",
  },
  evals: {
    evaluator_created: "evals.evaluator_created",
    evaluator_updated: "evals.evaluator_updated",
    experiment_created: "evals.experiment_created",
    experiment_updated: "evals.experiment_updated",
  },
  extractor: {
    created: "extractor.created",
    run_cancelled: "extractor.run_cancelled",
    run_triggered: "extractor.run_triggered",
    updated: "extractor.updated",
  },
  extractor_version: {
    created: "extractor_version.created",
  },
  file: {
    deleted: "file.deleted",
    uploaded: "file.uploaded",
  },
  inbound_email: {
    simulated: "inbound_email.simulated",
  },
  email_message: {
    composed: "email_message.composed",
    replied: "email_message.replied",
    released: "email_message.released",
    discarded: "email_message.discarded",
    accepted: "email_message.accepted",
  },
  email_policy: {
    created: "email_policy.created",
    updated: "email_policy.updated",
    deleted: "email_policy.deleted",
    attached: "email_policy.attached",
    detached: "email_policy.detached",
  },
  mailbox: {
    archived: "mailbox.archived",
    created: "mailbox.created",
    unarchived: "mailbox.unarchived",
    updated: "mailbox.updated",
  },
  invitation: {
    accepted: "invitation.accepted",
    canceled: "invitation.canceled",
    created: "invitation.created",
    declined: "invitation.declined",
    deleted: "invitation.deleted",
    resent: "invitation.resent",
  },
  linked_resource: {
    created: "linked_resource.created",
    deleted: "linked_resource.deleted",
  },
  oauth_grant: {
    created: "oauth_grant.created",
    revoked: "oauth_grant.revoked",
  },
  oauth_client: {
    created: "oauth_client.created",
    key_registered: "oauth_client.key_registered",
    key_revoked: "oauth_client.key_revoked",
    revoked: "oauth_client.revoked",
    token_issued: "oauth_client.token_issued",
    updated: "oauth_client.updated",
  },
  member: {
    removed: "member.removed",
  },
  custom_app: {
    deployed: "custom_app.deployed",
    job_triggered: "custom_app.job_triggered",
    record_created: "custom_app.record_created",
    record_updated: "custom_app.record_updated",
    record_deleted: "custom_app.record_deleted",
    record_batch: "custom_app.record_batch",
    rolled_back: "custom_app.rolled_back",
    deleted: "custom_app.deleted",
    archived: "custom_app.archived",
    restored: "custom_app.restored",
  },
  remote_desktop_target: {
    created: "remote_desktop_target.created",
    removed: "remote_desktop_target.removed",
    renamed: "remote_desktop_target.renamed",
  },
  remote_desktop_desk: {
    created: "remote_desktop_desk.created",
    disabled: "remote_desktop_desk.disabled",
    enabled: "remote_desktop_desk.enabled",
    removed: "remote_desktop_desk.removed",
    updated: "remote_desktop_desk.updated",
  },
  routine: {
    created: "routine.created",
    updated: "routine.updated",
    deleted: "routine.deleted",
    triggered: "routine.triggered",
  },
  bridge: {
    connection_test: "bridge.connection_test",
    created: "bridge.created",
    key_rotated: "bridge.key_rotated",
    remote_desktop_session: "bridge.remote_desktop_session",
    removed: "bridge.removed",
    renamed: "bridge.renamed",
  },
  schedule: {
    created: "schedule.created",
    updated: "schedule.updated",
    deleted: "schedule.deleted",
    triggered: "schedule.triggered",
  },
  scim: {
    directory: {
      activated: "scim.directory.activated",
      deleted: "scim.directory.deleted",
    },
    group: {
      deleted: "scim.group.deleted",
      upserted: "scim.group.upserted",
      user_added: "scim.group.user_added",
      user_removed: "scim.group.user_removed",
    },
    user: {
      deleted: "scim.user.deleted",
      upserted: "scim.user.upserted",
    },
  },
  signal: {
    created: "signal.created",
  },
  suggestion: {
    accepted: "suggestion.accepted",
    declined: "suggestion.declined",
  },
  simulation_world: {
    configuration_installed: "simulation_world.configuration_installed",
    configuration_opened: "simulation_world.configuration_opened",
    created: "simulation_world.created",
  },
  super_admin: {
    api_key_created: "super_admin.api_key_created",
    api_key_revoked: "super_admin.api_key_revoked",
    api_key_updated: "super_admin.api_key_updated",
    classifier_primary_model_updated: "super_admin.classifier_primary_model_updated",
    dataset_load_enqueued: "super_admin.dataset_load_enqueued",
    demo_reseed_enqueued: "super_admin.demo_reseed_enqueued",
    improver_run_requested: "super_admin.improver_run_requested",
    grader_triage_requested: "super_admin.grader_triage_requested",
    demo_settings_removed: "super_admin.demo_settings_removed",
    demo_settings_updated: "super_admin.demo_settings_updated",
    eval_annotation_created: "super_admin.eval_annotation_created",
    eval_experiment_created: "super_admin.eval_experiment_created",
    eval_experiment_dispatched: "super_admin.eval_experiment_dispatched",
    eval_file_registered: "super_admin.eval_file_registered",
    feature_flag_disabled: "super_admin.feature_flag_disabled",
    feature_flag_enabled: "super_admin.feature_flag_enabled",
    outlook_categories_backfill_requested: "super_admin.outlook_categories_backfill_requested",
    outlook_categories_backfill_started: "super_admin.outlook_categories_backfill_started",
    outlook_categories_backfill_completed: "super_admin.outlook_categories_backfill_completed",
    outlook_categories_backfill_failed: "super_admin.outlook_categories_backfill_failed",
    global_skills_seeded: "super_admin.global_skills_seeded",
    graph_doctor_run: "super_admin.graph_doctor_run",
    inbound_email_simulated: "super_admin.inbound_email_simulated",
    integration_connectivity_tested: "super_admin.integration_connectivity_tested",
    integration_tool_dryrun: "super_admin.integration_tool_dryrun",
    invitation_canceled: "super_admin.invitation_canceled",
    invitation_deleted: "super_admin.invitation_deleted",
    invitation_resent: "super_admin.invitation_resent",
    invitation_sent: "super_admin.invitation_sent",
    org_member_role_changed: "super_admin.org_member_role_changed",
    organization_created: "super_admin.organization_created",
    organization_deleted: "super_admin.organization_deleted",
    organization_domain_pre_verified: "super_admin.organization_domain_pre_verified",
    organization_domain_verified: "super_admin.organization_domain_verified",
    organization_updated: "super_admin.organization_updated",
    organization_workos_created_and_linked: "super_admin.organization_workos_created_and_linked",
    organization_workos_linked: "super_admin.organization_workos_linked",
    organization_workos_unlinked: "super_admin.organization_workos_unlinked",
    // One row per staff ops MCP tool call (`/internal/ops/mcp`), in the workspace it read.
    ops_read: "super_admin.ops_read",
    // Read-only remote mailbox browser: one started + one terminal record per
    // request, written into the mailbox's OWN workspace so the customer sees
    // that we looked at their live mail. Nothing is persisted by these reads.
    remote_mailbox_folders_read: "super_admin.remote_mailbox_folders_read",
    remote_mailbox_message_read: "super_admin.remote_mailbox_message_read",
    remote_mailbox_messages_read: "super_admin.remote_mailbox_messages_read",
    // Downloading a remote message's raw MIME — still a read, but a read of
    // the WHOLE message including every attachment byte, so it is recorded
    // separately from opening the message in the browser.
    remote_mailbox_message_raw_read: "super_admin.remote_mailbox_message_raw_read",
    // The one WRITE on the remote-mailbox surface: a single message pulled
    // into the customer's workspace through the normal ingest path.
    remote_mailbox_message_imported: "super_admin.remote_mailbox_message_imported",
    sharepoint_file_read: "super_admin.sharepoint_file_read",
    skill_created: "super_admin.skill_created",
    skill_deleted: "super_admin.skill_deleted",
    skill_updated: "super_admin.skill_updated",
    super_admin_flag_changed: "super_admin.super_admin_flag_changed",
    system_agent_updated: "super_admin.system_agent_updated",
    test_email_sent: "super_admin.test_email_sent",
    user_created: "super_admin.user_created",
    user_password_changed: "super_admin.user_password_changed",
    user_workos_linked: "super_admin.user_workos_linked",
    workspace_config_draft_saved: "super_admin.workspace_config_draft_saved",
    workspace_config_bindings_saved: "super_admin.workspace_config_bindings_saved",
    workspace_config_exported: "super_admin.workspace_config_exported",
    workspace_config_import_presigned: "super_admin.workspace_config_import_presigned",
    workspace_config_imported: "super_admin.workspace_config_imported",
    platform_model_policy_updated: "super_admin.platform_model_policy_updated",
    platform_document_parser_policy_updated: "super_admin.platform_document_parser_policy_updated",
    workspace_created: "super_admin.workspace_created",
    workspace_extraction_models_updated: "super_admin.workspace_extraction_models_updated",
    workspace_member_added: "super_admin.workspace_member_added",
    workspace_member_removed: "super_admin.workspace_member_removed",
    workspace_model_credential_created: "super_admin.workspace_model_credential_created",
    workspace_provider_credential_created: "super_admin.workspace_provider_credential_created",
    workspace_provider_credential_rotated: "super_admin.workspace_provider_credential_rotated",
    workspace_provider_credential_revoked: "super_admin.workspace_provider_credential_revoked",
    workspace_model_gateway_verified: "super_admin.workspace_model_gateway_verified",
    workspace_model_route_tested: "super_admin.workspace_model_route_tested",
    workspace_model_policy_deleted: "super_admin.workspace_model_policy_deleted",
    workspace_model_policy_updated: "super_admin.workspace_model_policy_updated",
    workspace_document_parser_policy_updated:
      "super_admin.workspace_document_parser_policy_updated",
    workspace_updated: "super_admin.workspace_updated",
  },
  system_integration: {
    updated: "system_integration.updated",
  },
  task: {
    created: "task.created",
    deleted: "task.deleted",
    resolved: "task.resolved",
    updated: "task.updated",
  },
  task_comment: {
    created: "task_comment.created",
    updated: "task_comment.updated",
  },
  task_execution: {
    canceled: "task_execution.canceled",
    created: "task_execution.created",
    retried: "task_execution.retried",
    run_started: "task_execution.run_started",
  },
  task_execution_action: {
    created: "task_execution_action.created",
  },
  task_linked_resource: {
    created: "task_linked_resource.created",
  },
  task_participant: {
    added: "task_participant.added",
    removed: "task_participant.removed",
  },
  task_template: {
    created: "task_template.created",
    deleted: "task_template.deleted",
    updated: "task_template.updated",
  },
  vault: {
    credential_created: "vault.credential_created",
    credential_revoked: "vault.credential_revoked",
    credential_rotated: "vault.credential_rotated",
    credential_updated: "vault.credential_updated",
    revocation_test_run: "vault.revocation_test_run",
    workspace_config_updated: "vault.workspace_config_updated",
  },
  verification: {
    deleted: "verification.deleted",
  },
  verification_run: {
    deleted: "verification_run.deleted",
  },
  watcher: {
    added: "watcher.added",
    removed: "watcher.removed",
  },
  webhook_endpoint: {
    /** The delivery worker turned the endpoint off; `context.metadata.reason` says why. */
    auto_disabled: "webhook_endpoint.auto_disabled",
    disabled: "webhook_endpoint.disabled",
    enabled: "webhook_endpoint.enabled",
  },
  workflow: {
    archived: "workflow.archived",
    created: "workflow.created",
    draft_discarded: "workflow.draft_discarded",
    draft_saved: "workflow.draft_saved",
    published: "workflow.published",
    run_triggered: "workflow.run_triggered",
  },
  workflow_run: {
    created: "workflow_run.created",
    rerun_triggered: "workflow_run.rerun_triggered",
  },
  workspace: {
    config_import: "workspace.config_import",
    config_import_presigned: "workspace.config_import_presigned",
    ip_allowlist_updated: "workspace.ip_allowlist_updated",
    updated: "workspace.updated",
  },
  workspace_skill: {
    created: "workspace_skill.created",
    deleted: "workspace_skill.deleted",
    updated: "workspace_skill.updated",
  },
  workspace_release: {
    discarded: "workspace_release.discarded",
    created: "workspace_release.created",
    updated: "workspace_release.updated",
    file_updated: "workspace_release.file_updated",
    file_deleted: "workspace_release.file_deleted",
    merged: "workspace_release.merged",
    imported: "workspace_release.imported",
    downloaded: "workspace_release.downloaded",
    rebased: "workspace_release.rebased",
    conflict_resolved: "workspace_release.conflict_resolved",
    validated: "workspace_release.validated",
    frozen: "workspace_release.frozen",
    activated: "workspace_release.activated",
    format_upgraded: "workspace_release.format_upgraded",
  },
} as const;

type Leaf<T> = T extends string ? T : T extends Record<string, unknown> ? Leaf<T[keyof T]> : never;

export type AuditAction = Leaf<typeof AUDIT_ACTIONS>;

function flatten(node: unknown, out: Set<string>): void {
  if (typeof node === "string") {
    out.add(node);
    return;
  }
  if (node && typeof node === "object") {
    for (const value of Object.values(node as Record<string, unknown>)) {
      flatten(value, out);
    }
  }
}

const allActions = new Set<string>();
flatten(AUDIT_ACTIONS, allActions);

export const ALL_AUDIT_ACTIONS: ReadonlySet<AuditAction> = allActions as ReadonlySet<AuditAction>;

// ---------------------------------------------------------------------------
// Retired vocabulary — READABLE, NEVER WRITABLE.
//
// `audit_logs` is append-only and tamper-evident: `event_hash` chains over the
// previous row's hash (packages/server/src/audit/integrity.ts). A row that
// says `connection.created` is not wrong — that is what the system called the
// event in 2026-06, before #1595 renamed the concept to `integration`. So we
// never rewrite the stored string, and we never relabel it to its modern
// equivalent at render time either. We widen the READ allowlist instead.
//
// Enforcement that no new row can carry one:
//   * `WritableAuditAction<A>` below resolves a retired literal to `never`,
//     and every recorder entry point types its `action` as
//     `A & WritableAuditAction<A>` — so `recordAudit({ action:
//     "connection.created" })` is a compile error at every call site.
//   * `assertWritableAuditAction()` backstops that at runtime for callers
//     that reach the recorder through an `as` cast or plain JS.
//
// Membership here is not a guess: each name is the pre-rename side of a
// mapping in packages/db/migrations/0206_rename_connection_to_integration.sql,
// cross-checked against the action catalog as it stood immediately before the
// rename (`git show ad2d79157^:packages/types/src/audit-actions.ts`). 0206's
// rewrite never applied — it named the wrong table AND ran under FORCE RLS on
// a table with no UPDATE policy — so these rows are still in production today.
//
// A retired name only belongs here if a row can actually still hold it.
// Counter-example: `email_message.moved_to_inbox` is NOT listed. Migration
// 0281 rewrites it to `email_message.accepted` and then proves the rewrite
// landed with a validated CHECK constraint (heap scan, not RLS-filtered), so
// after 0281 no row can carry it.
// ---------------------------------------------------------------------------

// Visibility tier each name held when it was retired. Classified explicitly,
// exactly like the live allowlists: a retired super-admin action must never
// become end-user readable just because it is old.
export const RETIRED_AUDIT_ACTIONS = {
  "super_admin.lab_fixture_created": "super_admin",
  "super_admin.lab_fixture_deleted": "super_admin",
  "super_admin.lab_fixture_run_enqueued": "super_admin",
  "enrichment.created": "end_user",
  // Insurance catalog routes, deleted with the catalog tables.
  "insurance.account_created": "end_user",
  "insurance.account_updated": "end_user",
  "insurance.coverage.created": "end_user",
  "insurance.coverage.deleted": "end_user",
  "insurance.coverage.updated": "end_user",
  "insurance.field_definition.created": "end_user",
  "insurance.field_definition.deleted": "end_user",
  "insurance.field_definition.updated": "end_user",
  "insurance.product_line.created": "end_user",
  "insurance.product_line.deleted": "end_user",
  "insurance.product_line.updated": "end_user",
  "insurance.schema_assignment.created": "end_user",
  "insurance.schema_assignment.deleted": "end_user",
  "insurance.schema_assignment.updated": "end_user",
  "insurance.schema_definition.created": "end_user",
  "insurance.schema_definition.deleted": "end_user",
  "insurance.schema_definition.updated": "end_user",
  "insurance.submission.created": "end_user",
  "insurance.submission_artifacts_attached": "end_user",
  "insurance.submission_coverage.created": "end_user",
  "insurance.submission_coverage.deleted": "end_user",
  "insurance.submission_created": "end_user",
  "insurance.submission_updated": "end_user",
  "submission_output.attached": "end_user",
  // Custom (config-driven) integrations, deleted with the feature (QUO-929).
  "integration_config.activated": "end_user",
  "integration_config.created": "end_user",
  "integration_config.credential_bound": "end_user",
  "integration_config.credentials_provisioned": "end_user",
  "integration_config.dryrun": "end_user",
  // 0206: `connection.` → `integration.`
  "connection.archived": "end_user",
  "connection.created": "end_user",
  "connection.file_imported": "end_user",
  "connection.permission_policy_updated": "end_user",
  "connection.provider_enabled": "end_user",
  "connection.revoked": "end_user",
  "connection.unarchived": "end_user",
  "connection.updated": "end_user",
  // 0206: `connection_grant.` → `integration_grant.`
  "connection_grant.created": "end_user",
  "connection_grant.revoked": "end_user",
  // 0206: `system_connection.` → `system_integration.`
  "system_connection.updated": "end_user",
  // 0419: `runner.` → `bridge.`. Not rewritten, because each row's hash covers its action.
  "runner.created": "end_user",
  "runner.key_rotated": "end_user",
  "runner.remote_desktop_session": "end_user",
  "runner.removed": "end_user",
  "runner.renamed": "end_user",
} as const satisfies Readonly<Record<string, "end_user" | "super_admin">>;

export type RetiredAuditAction = keyof typeof RETIRED_AUDIT_ACTIONS;

export const ALL_RETIRED_AUDIT_ACTIONS: ReadonlySet<RetiredAuditAction> = new Set(
  Object.keys(RETIRED_AUDIT_ACTIONS) as RetiredAuditAction[],
);

export const END_USER_RETIRED_AUDIT_ACTIONS: ReadonlySet<RetiredAuditAction> = new Set(
  (Object.entries(RETIRED_AUDIT_ACTIONS) as [RetiredAuditAction, "end_user" | "super_admin"][])
    .filter(([, tier]) => tier === "end_user")
    .map(([action]) => action),
);

export function isRetiredAuditAction(action: string): action is RetiredAuditAction {
  return ALL_RETIRED_AUDIT_ACTIONS.has(action as RetiredAuditAction);
}

/**
 * Compile-time write guard. Resolves a retired literal to `never`, so
 * `recordAudit({ action: "connection.created" })` fails to typecheck while a
 * non-retired literal — and a plain `string`, which `apps/agent`'s
 * still-uncatalogued vocabulary relies on — passes through unchanged.
 *
 * Used as `action: A & WritableAuditAction<A>` so `A` is inferred from the
 * naked member; `A & never` is `never`, which nothing is assignable to.
 */
export type WritableAuditAction<A extends string> = A extends RetiredAuditAction ? never : A;

/**
 * Runtime backstop for the write path — `WritableAuditAction` already rejects
 * a retired literal at compile time; this catches the callers that cast.
 * Throws rather than silently downgrading, because a write of a retired name
 * would create a row the vocabulary says cannot exist, on a table nothing can
 * go back and correct.
 */
export function assertWritableAuditAction(action: string): void {
  if (isRetiredAuditAction(action)) {
    throw new Error(
      `audit-actions: "${action}" is a RETIRED audit action and cannot be recorded — retired names exist only on historical rows. Record the current action for this event instead (see AUDIT_ACTIONS in packages/types/src/audit-actions.ts).`,
    );
  }
}

// Partition `ALL_AUDIT_ACTIONS` into two explicit, disjoint sets. These are
// hand-maintained ALLOWLISTS, not derived from `AUDIT_ACTIONS`. Adding a new
// leaf to `AUDIT_ACTIONS` does NOT automatically grant it end-user visibility
// — every leaf must be explicitly classified here or the module fails to load.
// The whole point: end-user visibility is opt-in, never default.

export const END_USER_AUDIT_ACTIONS: ReadonlySet<AuditAction> = new Set<AuditAction>([
  "workspace_github.proposal_requested",
  "workspace_github.repository_create_requested",
  "workspace_github.repository_seed_requested",
  "workspace_github.authorization_started",
  "workspace_github.authorized",
  "workspace_github.connected",
  "workspace_github.disconnected",
  "workspace_github.revision_retained",
  "workspace_github.archive_downloaded",
  // Agent configuration events carry names, status, model and version ids, never the operating
  // procedure or the config/toolsConfig blobs (agent.deleted omits them on purpose).
  "agent.created",
  "agent.deleted",
  "agent.updated",
  "agent.version.drafted",
  "agent.version.promoted",
  "agent.version.published",
  "agent_file.attached",
  "agent_file.detached",
  "agent_file.label_updated",
  "agent_linked_resource.created",
  "agent_linked_resource.deleted",
  "agent_skill.created",
  "agent_skill.deleted",
  "agent_skill.updated",
  "agent_thread.approval_approved",
  "agent_thread.approval_denied",
  "agent_thread.canceled",
  "agent_thread.input_request_replied",
  "agent_thread.listener_canceled",
  "agent_thread.message_queued",
  "agent_thread.question_answered",
  "agent_thread.queued_message_canceled",
  "analyzer_run.created",
  "api_key.created",
  "api_key.revoked",
  "classification.created",
  "classifier.created",
  "classifier.updated",
  "classifier_version.created",
  "computer_use.canceled",
  "custom_model.archived",
  "custom_model.created",
  "custom_model.updated",
  "custom_model.records_imported",
  "custom_record.created",
  "custom_record.deleted",
  "custom_record.updated",
  "integration.archived",
  "integration.created",
  "integration.credentials_tested",
  "integration.deleted",
  "integration.file_imported",
  "integration.mailbox_folders_updated",
  "integration.permission_policy_updated",
  "integration.provider_enabled",
  "integration.resync_triggered",
  "integration.revoked",
  "integration.tool_dryrun",
  "integration.unarchived",
  "integration.updated",
  "integration_grant.created",
  "integration_grant.revoked",
  "integration_grant.updated",
  "entity_file.attached",
  "entity_file.detached",
  "evals.evaluator_created",
  "evals.evaluator_updated",
  "evals.experiment_created",
  "evals.experiment_updated",
  "extractor.created",
  "extractor.run_cancelled",
  "extractor.run_triggered",
  "extractor.updated",
  "extractor_version.created",
  "file.deleted",
  "file.uploaded",
  "inbound_email.simulated",
  "email_message.composed",
  "email_message.replied",
  "email_message.released",
  "email_message.discarded",
  "email_message.accepted",
  "email_policy.created",
  "email_policy.updated",
  "email_policy.deleted",
  "email_policy.attached",
  "email_policy.detached",
  "invitation.accepted",
  "invitation.canceled",
  "invitation.created",
  "invitation.declined",
  "invitation.deleted",
  "invitation.resent",
  "linked_resource.created",
  "linked_resource.deleted",
  "mailbox.archived",
  "mailbox.created",
  "mailbox.unarchived",
  "mailbox.updated",
  "outbound_oauth_connection.created",
  "outbound_oauth_connection.credential_rotated",
  "outbound_oauth_connection.credentials_tested",
  "outbound_oauth_connection.disabled",
  "outbound_oauth_connection.enabled",
  "outbound_oauth_connection.renamed",
  "outbound_oauth_connection.updated",
  "oauth_grant.created",
  "oauth_grant.revoked",
  "oauth_client.created",
  "oauth_client.key_registered",
  "oauth_client.key_revoked",
  "oauth_client.revoked",
  "oauth_client.token_issued",
  "oauth_client.updated",
  "member.removed",
  "custom_app.deployed",
  "custom_app.job_triggered",
  "custom_app.record_created",
  "custom_app.record_updated",
  "custom_app.record_deleted",
  "custom_app.record_batch",
  "custom_app.rolled_back",
  "custom_app.deleted",
  "custom_app.archived",
  "custom_app.restored",
  "remote_desktop_target.created",
  "remote_desktop_target.removed",
  "remote_desktop_target.renamed",
  "remote_desktop_desk.created",
  "remote_desktop_desk.disabled",
  "remote_desktop_desk.enabled",
  "remote_desktop_desk.removed",
  "remote_desktop_desk.updated",
  "routine.created",
  "routine.updated",
  "routine.deleted",
  "routine.triggered",
  "bridge.connection_test",
  "bridge.created",
  "bridge.key_rotated",
  "bridge.remote_desktop_session",
  "bridge.removed",
  "bridge.renamed",
  "schedule.created",
  "schedule.updated",
  "schedule.deleted",
  "schedule.triggered",
  "scim.directory.activated",
  "scim.directory.deleted",
  "scim.group.deleted",
  "scim.group.upserted",
  "scim.group.user_added",
  "scim.group.user_removed",
  "scim.user.deleted",
  "scim.user.upserted",
  "signal.created",
  "suggestion.accepted",
  "suggestion.declined",
  "simulation_world.configuration_installed",
  "simulation_world.configuration_opened",
  "simulation_world.created",
  "system_integration.updated",
  "task.created",
  "task.deleted",
  "task.resolved",
  "task.updated",
  "task_comment.created",
  "task_comment.updated",
  "task_execution.canceled",
  "task_execution.created",
  "task_execution.retried",
  "task_execution.run_started",
  "task_execution_action.created",
  "task_linked_resource.created",
  "task_participant.added",
  "task_participant.removed",
  "task_template.created",
  "task_template.deleted",
  "task_template.updated",
  "vault.credential_created",
  "vault.credential_updated",
  "vault.credential_revoked",
  "vault.credential_rotated",
  "vault.revocation_test_run",
  "vault.workspace_config_updated",
  "verification.deleted",
  "verification_run.deleted",
  "watcher.added",
  "watcher.removed",
  "workflow.archived",
  "workflow.created",
  "webhook_endpoint.auto_disabled",
  "webhook_endpoint.disabled",
  "webhook_endpoint.enabled",
  "workflow.draft_discarded",
  "workflow.draft_saved",
  "workflow.published",
  "workflow.run_triggered",
  "workflow_run.created",
  "workflow_run.rerun_triggered",
  "workspace.config_import",
  "workspace.config_import_presigned",
  "workspace.ip_allowlist_updated",
  "workspace.updated",
  "workspace_skill.created",
  "workspace_skill.deleted",
  "workspace_skill.updated",
  "workspace_release.created",
  "workspace_release.discarded",
  "workspace_release.updated",
  "workspace_release.file_updated",
  "workspace_release.file_deleted",
  "workspace_release.merged",
  "workspace_release.imported",
  "workspace_release.downloaded",
  "workspace_release.rebased",
  "workspace_release.conflict_resolved",
  "workspace_release.validated",
  "workspace_release.frozen",
  "workspace_release.activated",
  "workspace_release.format_upgraded",
]);

export const SUPER_ADMIN_AUDIT_ACTIONS: ReadonlySet<AuditAction> = new Set<AuditAction>([
  "super_admin.api_key_created",
  "super_admin.api_key_revoked",
  "super_admin.api_key_updated",
  "computer_use.steered",
  "super_admin.classifier_primary_model_updated",
  "super_admin.dataset_load_enqueued",
  "super_admin.demo_reseed_enqueued",
  "super_admin.demo_settings_removed",
  "super_admin.demo_settings_updated",
  "super_admin.eval_annotation_created",
  "super_admin.eval_experiment_created",
  "super_admin.eval_experiment_dispatched",
  "super_admin.eval_file_registered",
  "super_admin.improver_run_requested",
  "super_admin.grader_triage_requested",
  "super_admin.feature_flag_disabled",
  "super_admin.feature_flag_enabled",
  "super_admin.outlook_categories_backfill_requested",
  "super_admin.outlook_categories_backfill_started",
  "super_admin.outlook_categories_backfill_completed",
  "super_admin.outlook_categories_backfill_failed",
  "super_admin.global_skills_seeded",
  "super_admin.graph_doctor_run",
  "super_admin.inbound_email_simulated",
  "super_admin.integration_connectivity_tested",
  "super_admin.integration_tool_dryrun",
  "super_admin.invitation_canceled",
  "super_admin.invitation_deleted",
  "super_admin.invitation_resent",
  "super_admin.invitation_sent",
  "super_admin.org_member_role_changed",
  "super_admin.organization_created",
  "super_admin.organization_deleted",
  "super_admin.organization_domain_pre_verified",
  "super_admin.organization_domain_verified",
  "super_admin.organization_updated",
  "super_admin.organization_workos_created_and_linked",
  "super_admin.organization_workos_linked",
  "super_admin.organization_workos_unlinked",
  "super_admin.ops_read",
  "super_admin.remote_mailbox_folders_read",
  "super_admin.remote_mailbox_message_imported",
  "super_admin.remote_mailbox_message_raw_read",
  "super_admin.remote_mailbox_message_read",
  "super_admin.remote_mailbox_messages_read",
  "super_admin.sharepoint_file_read",
  "super_admin.skill_created",
  "super_admin.skill_deleted",
  "super_admin.skill_updated",
  "super_admin.super_admin_flag_changed",
  "super_admin.system_agent_updated",
  "super_admin.test_email_sent",
  "super_admin.user_created",
  "super_admin.user_password_changed",
  "super_admin.user_workos_linked",
  "super_admin.workspace_config_draft_saved",
  "super_admin.workspace_config_bindings_saved",
  "super_admin.workspace_config_exported",
  "super_admin.workspace_config_import_presigned",
  "super_admin.workspace_config_imported",
  "super_admin.platform_model_policy_updated",
  "super_admin.platform_document_parser_policy_updated",
  "super_admin.workspace_created",
  "super_admin.workspace_extraction_models_updated",
  "super_admin.workspace_member_added",
  "super_admin.workspace_member_removed",
  "super_admin.workspace_model_credential_created",
  "super_admin.workspace_provider_credential_created",
  "super_admin.workspace_provider_credential_rotated",
  "super_admin.workspace_provider_credential_revoked",
  "super_admin.workspace_model_gateway_verified",
  "super_admin.workspace_model_route_tested",
  "super_admin.workspace_model_policy_deleted",
  "super_admin.workspace_model_policy_updated",
  "super_admin.workspace_document_parser_policy_updated",
  "super_admin.workspace_updated",
]);

// Read allowlists. These — not the write-side sets above — are what the
// `/v1/audit-logs` and super-admin routes filter on, because a historical row
// carrying a retired action must still be returned. They are supersets: the
// live sets keep their exact previous membership, so nothing about the
// end-user/super-admin split is weakened.
export const END_USER_READABLE_AUDIT_ACTIONS: ReadonlySet<string> = new Set<string>([
  ...END_USER_AUDIT_ACTIONS,
  ...END_USER_RETIRED_AUDIT_ACTIONS,
]);

export const ALL_READABLE_AUDIT_ACTIONS: ReadonlySet<string> = new Set<string>([
  ...ALL_AUDIT_ACTIONS,
  ...ALL_RETIRED_AUDIT_ACTIONS,
]);

// Invariants: disjoint + total coverage. Throws at import time on violation.
// Total coverage means every leaf in `ALL_AUDIT_ACTIONS` is classified into
// exactly one set — so a newly-added action in `AUDIT_ACTIONS` cannot ship
// without an explicit security decision. The retired vocabulary is a THIRD
// set that must not intersect either: a name is current or retired, never
// both, and that disjointness is what makes the `AuditAction` union a
// type-level write-rejection for retired names.
{
  const overlap: string[] = [];
  for (const a of END_USER_AUDIT_ACTIONS) {
    if (SUPER_ADMIN_AUDIT_ACTIONS.has(a)) overlap.push(a);
  }
  if (overlap.length > 0) {
    throw new Error(
      `audit-actions: actions appear in BOTH END_USER_AUDIT_ACTIONS and SUPER_ADMIN_AUDIT_ACTIONS: ${overlap.join(", ")}`,
    );
  }

  const unknown: string[] = [];
  for (const a of END_USER_AUDIT_ACTIONS) {
    if (!ALL_AUDIT_ACTIONS.has(a)) unknown.push(a);
  }
  for (const a of SUPER_ADMIN_AUDIT_ACTIONS) {
    if (!ALL_AUDIT_ACTIONS.has(a)) unknown.push(a);
  }
  if (unknown.length > 0) {
    throw new Error(
      `audit-actions: classified actions not present in AUDIT_ACTIONS catalog: ${unknown.join(", ")}`,
    );
  }

  const unclassified: string[] = [];
  for (const a of ALL_AUDIT_ACTIONS) {
    if (!END_USER_AUDIT_ACTIONS.has(a) && !SUPER_ADMIN_AUDIT_ACTIONS.has(a)) {
      unclassified.push(a);
    }
  }
  if (unclassified.length > 0) {
    throw new Error(
      `audit-actions: new audit actions must be classified as end-user or super-admin visible. Unclassified: ${unclassified.join(", ")}`,
    );
  }

  const resurrected: string[] = [];
  for (const a of ALL_RETIRED_AUDIT_ACTIONS) {
    if (allActions.has(a)) resurrected.push(a);
  }
  if (resurrected.length > 0) {
    throw new Error(
      `audit-actions: actions appear in BOTH AUDIT_ACTIONS and RETIRED_AUDIT_ACTIONS: ${resurrected.join(", ")}. A retired name must stay out of the live catalog — that disjointness is what keeps \`AuditAction\` from admitting it on the write path. Remove it from one side.`,
    );
  }
}

export function isKnownAuditAction(action: string): action is AuditAction {
  return ALL_AUDIT_ACTIONS.has(action as AuditAction);
}
